export interface UserDetailsAccessRow {
  key: string;
  customerApplication: string;
  dashboards: string[];
  roles: string[];
}

export type UserDetailsReference = string | number | UserDetailsRecord | null | undefined;

interface UserDetailsRecord {
  _id?: string | number;
  id?: string | number;
  value?: string | number;
  key?: string | number;
  name?: string;
  title?: string;
  label?: string;
  customerApplication?: UserDetailsReference;
  customerApplicationId?: UserDetailsReference;
  customerApplicationName?: string;
  [key: string]: unknown;
}

export interface UserDetailsCustomerApplication extends UserDetailsRecord {
  name?: string;
}

export interface UserDetailsDashboard extends UserDetailsRecord {
  name?: string;
  customerApplication?: UserDetailsReference;
}

export interface UserDetailsRole extends UserDetailsRecord {
  name?: string;
  customerApplication?: UserDetailsReference;
  dashboards?: UserDetailsReference[];
}

export interface UserDetailsUser {
  customerApplications?: UserDetailsReference[];
  dashboards?: UserDetailsReference[];
  roles?: UserDetailsReference[];
}

interface BuildUserAccessRowsParams {
  user?: UserDetailsUser | null;
  applicationNames: Record<string, string>;
  dashboardDetailsById: Record<string, UserDetailsDashboard>;
  dashboardNames: Record<string, string>;
  roleDetailsById: Record<string, UserDetailsRole>;
  roleNames: Record<string, string>;
}

export type AccessRowField = "dashboards" | "roles";

export interface MapItemsToRowsParams {
  items: UserDetailsReference[];
  detailsById: Record<string, UserDetailsRecord>;
  namesById: Record<string, string>;
  rowField: AccessRowField;
  rowsByApplication: Map<string, UserDetailsAccessRow>;
  applicationNames: Record<string, string>;
}

const isUserDetailsRecord = (item: UserDetailsReference): item is UserDetailsRecord =>
  Boolean(item && typeof item === "object");

const isDisplayValue = (value: unknown): value is string | number =>
  (typeof value === "string" && value !== "") || typeof value === "number";

export const getUserDetailsItemId = (item: UserDetailsReference): string | null => {
  if (!item) return null;
  if (typeof item === "string" || typeof item === "number") return String(item);
  if (isUserDetailsRecord(item)) {
    const id = item._id || item.id || item.value || item.key;
    return id ? String(id) : null;
  }
  return null;
};

export const getUserDetailsItemName = (
  item: UserDetailsReference,
  key: string,
  namesById: Record<string, string> = {},
  options: { allowPrimitiveDisplay?: boolean; unknownLabel?: string } = {}
): string | null => {
  const unknownLabel = options.unknownLabel ?? "Unknown";
  const itemId = getUserDetailsItemId(item);
  if (itemId && namesById[itemId]) return namesById[itemId];
  if (typeof item === "string" || typeof item === "number") {
    return options.allowPrimitiveDisplay ? String(item) : unknownLabel;
  }
  if (isUserDetailsRecord(item)) {
    const keyedValue = item[key];
    return [keyedValue, item.name, item.title, item.label].find(isDisplayValue)?.toString() || unknownLabel;
  }
  return null;
};

const getCustomerApplicationKey = (item: UserDetailsRecord | null) => {
  const customerApplication = item?.customerApplication || item?.customerApplicationId;
  return getUserDetailsItemId(customerApplication)
    || (isDisplayValue(customerApplication) ? String(customerApplication) : null)
    || item?.customerApplicationName
    || null;
};

const addUnique = (items: string[], value: string | null) => {
  if (value && !items.includes(value)) items.push(value);
};

const createEmptyAccessRow = (
  key: string,
  customerApplication: string
): UserDetailsAccessRow => ({
  key,
  customerApplication,
  dashboards: [],
  roles: [],
});

const getKnownCustomerApplicationName = (
  item: UserDetailsRecord | null,
  applicationKey: string | null,
  applicationNames: Record<string, string>,
  rowsByApplication: Map<string, UserDetailsAccessRow>
) => {
  if (!applicationKey) return null;

  return applicationNames[applicationKey]
    || rowsByApplication.get(applicationKey)?.customerApplication
    || item?.customerApplicationName
    || null;
};

export const mapItemsToRows = ({
  items,
  detailsById,
  namesById,
  rowField,
  rowsByApplication,
  applicationNames,
}: MapItemsToRowsParams) => {
  items.forEach((item) => {
    const itemId = getUserDetailsItemId(item);
    const itemDetails = itemId ? detailsById[itemId] : null;
    const itemRecord = isUserDetailsRecord(item) ? { ...itemDetails, ...item } : itemDetails;
    const applicationKey = getCustomerApplicationKey(itemRecord);
    const applicationName = getKnownCustomerApplicationName(
      itemRecord,
      applicationKey,
      applicationNames,
      rowsByApplication
    );
    if (!applicationName) return;

    const rowKey = String(applicationKey);

    if (!rowsByApplication.has(rowKey)) {
      rowsByApplication.set(rowKey, createEmptyAccessRow(rowKey, applicationName));
    }

    const row = rowsByApplication.get(rowKey)!;
    addUnique(row[rowField], getUserDetailsItemName(itemRecord || item, "name", namesById));
  });
};

export const buildUserAccessRows = ({
  user,
  applicationNames,
  dashboardDetailsById,
  dashboardNames,
  roleDetailsById,
  roleNames,
}: BuildUserAccessRowsParams): UserDetailsAccessRow[] => {
  const rowsByApplication = new Map<string, UserDetailsAccessRow>();

  (user?.customerApplications || []).forEach((application) => {
    const applicationId = getUserDetailsItemId(application);
    const applicationName = getUserDetailsItemName(application, "name", applicationNames);
    if (applicationName && applicationName !== "Unknown") {
      rowsByApplication.set(
        applicationId || applicationName,
        createEmptyAccessRow(applicationId || applicationName, applicationName)
      );
    }
  });

  mapItemsToRows({
    items: user?.dashboards || [],
    detailsById: dashboardDetailsById,
    namesById: dashboardNames,
    rowField: "dashboards",
    rowsByApplication,
    applicationNames,
  });

  mapItemsToRows({
    items: user?.roles || [],
    detailsById: roleDetailsById,
    namesById: roleNames,
    rowField: "roles",
    rowsByApplication,
    applicationNames,
  });

  // Dashboards granted via a role aren't included in user.dashboards, so pull them in too.
  const roleInheritedDashboards = (user?.roles || []).flatMap((role) => {
    const roleId = getUserDetailsItemId(role);
    const roleDetails = roleId ? roleDetailsById[roleId] : null;
    // Prefer fields already on the user's role reference, falling back to the fetched
    // role details (which is where `dashboards` normally comes from) when missing.
    const roleRecord: UserDetailsRole | null = isUserDetailsRecord(role)
      ? { ...roleDetails, ...role }
      : roleDetails;
    return roleRecord?.dashboards || [];
  });

  // addUnique in mapItemsToRows dedupes by display name, so a dashboard that's both
  // directly assigned and role-inherited only shows up once per application row.
  mapItemsToRows({
    items: roleInheritedDashboards,
    detailsById: dashboardDetailsById,
    namesById: dashboardNames,
    rowField: "dashboards",
    rowsByApplication,
    applicationNames,
  });

  return Array.from(rowsByApplication.values());
};